// src/hooks/useConnectivity.js
// Network connectivity with structured offline/online event handling.
//
// useConnectivity()      — online boolean, reconnect timestamp, event callbacks
// useOfflineBanner()     — whether to show the offline banner in a given feature
// useReconnectEffect(fn) — run a function once when coming back online

import { useState, useEffect, useCallback, useRef } from 'react';

// ── useConnectivity ───────────────────────────────────────────────
export function useConnectivity() {
  const [online,       setOnline]       = useState(() => navigator.onLine);
  const [lastOnlineAt, setLastOnlineAt] = useState(null);
  const [lastOfflineAt,setLastOfflineAt]= useState(null);

  useEffect(() => {
    const onOnline = () => {
      setOnline(true);
      setLastOnlineAt(Date.now());
    };
    const onOffline = () => {
      setOnline(false);
      setLastOfflineAt(Date.now());
    };
    window.addEventListener('online',  onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online',  onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // How long we've been offline in seconds (null if online)
  const offlineForSecs = !online && lastOfflineAt
    ? Math.floor((Date.now() - lastOfflineAt) / 1000)
    : null;

  return {
    online,
    offline: !online,
    lastOnlineAt,
    lastOfflineAt,
    offlineForSecs,
  };
}

// ── useOfflineBanner ──────────────────────────────────────────────
// Returns whether a feature's offline banner should be visible.
// Visible when: offline AND the feature has shown stale data for > gracePeriodMs.
export function useOfflineBanner(isStale, gracePeriodMs = 30_000) {
  const { online, lastOfflineAt } = useConnectivity();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (online) { setShow(false); return; }
    if (!isStale) { setShow(false); return; }

    // Show banner immediately if we've been offline > grace period
    const offlineFor = lastOfflineAt ? Date.now() - lastOfflineAt : 0;
    if (offlineFor >= gracePeriodMs) { setShow(true); return; }

    // Otherwise delay until grace period expires
    const remaining = gracePeriodMs - offlineFor;
    const id = setTimeout(() => setShow(true), remaining);
    return () => clearTimeout(id);
  }, [online, isStale, lastOfflineAt, gracePeriodMs]);

  return show;
}

// ── useReconnectEffect ────────────────────────────────────────────
// Runs `fn` exactly once when coming back online.
// Useful for triggering a manual refresh after network loss.
export function useReconnectEffect(fn, deps = []) {
  const wasOffline = useRef(false);
  const fnRef      = useRef(fn);
  fnRef.current    = fn;

  useEffect(() => {
    const onOffline = () => { wasOffline.current = true; };
    const onOnline  = () => {
      if (wasOffline.current) {
        wasOffline.current = false;
        fnRef.current();
      }
    };
    window.addEventListener('offline', onOffline);
    window.addEventListener('online',  onOnline);
    return () => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online',  onOnline);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
